import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useSelector } from '@tabeeb/store'
import { getQualificationsForInvitesEnabled } from '@tabeeb/modules/appConfigState/selectors'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import type { UserSearchInfoResponse } from '@tabeeb/contracts/main-api/swagger-main-api'

import { QualificationsQueryKeys } from '../queries'

export interface UseUsersQualificationsProps {
  users: UserSearchInfoResponse[]
  requiredQualificationIds?: number[] | null
}

function useUsersQualifications({ users, requiredQualificationIds }: UseUsersQualificationsProps) {
  const isQualificationsForInvitesEnabled = useSelector(getQualificationsForInvitesEnabled)
  const { certificateServiceApi: api } = useApi()

  const userGuids = useMemo(() => users.map((user) => user.IdentityGuid), [users])

  const isEnabled =
    isQualificationsForInvitesEnabled &&
    requiredQualificationIds != null &&
    requiredQualificationIds.length > 0 &&
    users.length > 0

  const qualificationsQuery = useQuery({
    queryKey: [QualificationsQueryKeys.UserQualifications, 'active-ids', userGuids],
    queryFn: () => api.userQualifications.getActiveIdsAsync({ UserGuids: userGuids }),
    enabled: isEnabled,
  })

  return useMemo(
    () => ({
      loadingQualifications: qualificationsQuery.isLoading || qualificationsQuery.isFetching,
      usersQualifications: qualificationsQuery.data ?? [],
    }),
    [qualificationsQuery.isLoading, qualificationsQuery.isFetching, qualificationsQuery.data]
  )
}

export default useUsersQualifications
